import "./style.css";

type EngineInfo = {
  name: string;
  metadata: string;
  filename: string;
  sample?: string;
  sampleDetail?: string;
};

type ModelInfo = {
  fuelName: string;
  cylinderCount: number;
  cylinderBankCount: number;
  displacementLiters: number;
  intakeCount: number;
  exhaustCount: number;
  impulseResponseCount: number;
  simulationFrequency: number;
  convolutionMix: number;
};

type Telemetry = {
  rpm: number;
  manifoldPressureKpa: number;
  intakeAfr: number;
  cylinderPressureKpa: number;
  exhaustPressureKpa: number;
};

type WorkerMessage =
  | { type: "ready"; engineName: string; model: ModelInfo }
  | { type: "audio"; samples: Int16Array; sampleRate: number }
  | { type: "progress"; progress: number; telemetry: Telemetry }
  | { type: "complete"; bytes: Uint8Array; engineName: string; sampleRate: number }
  | { type: "error"; message: string };

const publicAsset = (path: string) => `${import.meta.env.BASE_URL}${path}`;

const engines: Record<string, EngineInfo> = {
  "subaru-ej25": { name: "Subaru EJ25", metadata: "4 cylinders · 2.5 L · Boxer", filename: "subaru-ej25.wav", sample: publicAsset("samples/subaru-ej25.wav"), sampleDetail: "Full-quality CLI render · 10 seconds" },
  "toyota-2jz": { name: "Toyota 2JZ", metadata: "6 cylinders · 3.0 L · Inline", filename: "toyota-2jz.wav", sample: publicAsset("samples/toyota-2jz.wav"), sampleDetail: "Full-quality cached render · 5 seconds" },
  "gm-ls-v8": { name: "GM LS", metadata: "8 cylinders · Cross-plane V8", filename: "gm-ls-v8.wav", sample: publicAsset("samples/gm-ls-v8.wav"), sampleDetail: "Full-quality cached render · 5 seconds" },
  "ferrari-f136-v8": { name: "Ferrari F136", metadata: "8 cylinders · Flat-plane V8", filename: "ferrari-f136-v8.wav", sample: publicAsset("samples/ferrari-f136-v8.wav"), sampleDetail: "Full-quality cached render · 5 seconds" },
  "ferrari-f430-f136e": { name: "Ferrari F430", metadata: "8 cylinders · 4.3 L · 90° flat-plane V8 · Approximation", filename: "ferrari-f430-f136e.wav", sample: publicAsset("samples/ferrari-f430-f136e.wav"), sampleDetail: "Full-quality approximate cached render · 5 seconds" },
  "ferrari-412-t2-v12": { name: "Ferrari 412 T2", metadata: "12 cylinders · 3.0 L · F1 V12", filename: "ferrari-412-t2-v12.wav", sample: publicAsset("samples/ferrari-412-t2-v12.wav"), sampleDetail: "Full-quality cached render · 5 seconds" },
  "ferrari-enzo-f140b": { name: "Ferrari Enzo F140B", metadata: "12 cylinders · 6.0 L · 65° V12 · Approximation", filename: "ferrari-enzo-f140b.wav", sample: publicAsset("samples/ferrari-enzo-f140b.wav"), sampleDetail: "Full-quality approximate render · 5 seconds" },
  "lexus-lfa-v10": { name: "Lexus LFA", metadata: "10 cylinders · 4.8 L · V10", filename: "lexus-lfa-v10.wav", sample: publicAsset("samples/lexus-lfa-v10.wav"), sampleDetail: "Full-quality cached render · 5 seconds" },
  "merlin-v12": { name: "Rolls-Royce Merlin", metadata: "12 cylinders · Supercharged V12", filename: "merlin-v12.wav", sample: publicAsset("samples/merlin-v12.wav"), sampleDetail: "Full-quality cached render · 5 seconds" },
  "harley-shovelhead": { name: "Harley Shovelhead", metadata: "2 cylinders · 45° V-twin", filename: "harley-shovelhead.wav", sample: publicAsset("samples/harley-shovelhead.wav"), sampleDetail: "Full-quality cached render · 5 seconds" },
  "honda-vtec": { name: "Honda VTEC", metadata: "4 cylinders · Inline · VTEC", filename: "honda-vtec.wav", sample: publicAsset("samples/honda-vtec.wav"), sampleDetail: "Full-quality cached render · 5 seconds" },
};

const element = <T extends HTMLElement>(selector: string): T => {
  const match = document.querySelector<T>(selector);
  if (!match) throw new Error(`Missing element: ${selector}`);
  return match;
};

const enginePicker = element<HTMLDivElement>("#engine-picker");
const durationSelect = element<HTMLSelectElement>("#duration-select");
const throttleRange = element<HTMLInputElement>("#throttle-range");
const throttleOutput = element<HTMLOutputElement>("#throttle-output");
const generateButton = element<HTMLButtonElement>("#generate-button");
const sampleButton = element<HTMLButtonElement>("#sample-button");
const cancelButton = element<HTMLButtonElement>("#cancel-button");
const renderStatus = element<HTMLDivElement>("#render-status");
const statusLabel = element<HTMLParagraphElement>("#status-label");
const progressLabel = element<HTMLOutputElement>("#progress-label");
const renderProgress = element<HTMLProgressElement>("#render-progress");
const playerPanel = element<HTMLElement>("#player-panel");
const trackTitle = element<HTMLElement>("#track-title");
const trackDetail = element<HTMLElement>("#track-detail");
const audioPlayer = element<HTMLAudioElement>("#audio-player");
const downloadLink = element<HTMLAnchorElement>("#download-link");
const visualizerCanvas = element<HTMLCanvasElement>("#audio-visualizer");
const modelState = element<HTMLElement>("#model-state");
const modelEngine = element<HTMLElement>("#model-engine");
const modelGeometry = element<HTMLElement>("#model-geometry");
const modelDisplacement = element<HTMLElement>("#model-displacement");
const modelFrequency = element<HTMLElement>("#model-frequency");
const modelRouting = element<HTMLElement>("#model-routing");
const modelAcoustics = element<HTMLElement>("#model-acoustics");
const telemetryRpm = element<HTMLElement>("#telemetry-rpm");
const telemetryManifold = element<HTMLElement>("#telemetry-manifold");
const telemetryAfr = element<HTMLElement>("#telemetry-afr");
const telemetryCylinder = element<HTMLElement>("#telemetry-cylinder");
const telemetryExhaust = element<HTMLElement>("#telemetry-exhaust");

class OutputVisualizer {
  private analyser: AnalyserNode | undefined;
  private timeData = new Uint8Array(0);
  private frequencyData = new Uint8Array(0);
  private readonly context: CanvasRenderingContext2D | null;

  constructor(private readonly canvas: HTMLCanvasElement) {
    this.context = canvas.getContext("2d");
    requestAnimationFrame(this.draw);
  }

  attach(analyser?: AnalyserNode) {
    this.analyser = analyser;
    this.timeData = analyser ? new Uint8Array(analyser.fftSize) : new Uint8Array(0);
    this.frequencyData = analyser
      ? new Uint8Array(analyser.frequencyBinCount)
      : new Uint8Array(0);
  }

  private readonly draw = () => {
    requestAnimationFrame(this.draw);
    const drawing = this.context;
    if (!drawing) return;

    const width = this.canvas.clientWidth;
    const height = this.canvas.clientHeight;
    const pixelRatio = Math.min(2, window.devicePixelRatio || 1);
    const pixelWidth = Math.max(1, Math.round(width * pixelRatio));
    const pixelHeight = Math.max(1, Math.round(height * pixelRatio));
    if (this.canvas.width !== pixelWidth || this.canvas.height !== pixelHeight) {
      this.canvas.width = pixelWidth;
      this.canvas.height = pixelHeight;
      drawing.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    }

    const styles = getComputedStyle(document.documentElement);
    const background = styles.getPropertyValue("--control").trim();
    const line = styles.getPropertyValue("--line").trim();
    const accent = styles.getPropertyValue("--accent").trim();
    drawing.fillStyle = background;
    drawing.fillRect(0, 0, width, height);
    drawing.strokeStyle = line;
    drawing.lineWidth = 1;
    for (let x = width / 8; x < width; x += width / 8) {
      drawing.beginPath();
      drawing.moveTo(Math.round(x) + 0.5, 0);
      drawing.lineTo(Math.round(x) + 0.5, height);
      drawing.stroke();
    }
    drawing.beginPath();
    drawing.moveTo(0, Math.round(height / 2) + 0.5);
    drawing.lineTo(width, Math.round(height / 2) + 0.5);
    drawing.stroke();

    if (!this.analyser) return;
    this.analyser.getByteTimeDomainData(this.timeData);
    this.analyser.getByteFrequencyData(this.frequencyData);

    const barCount = Math.min(72, Math.floor(width / 5));
    const binsPerBar = Math.max(1, Math.floor(this.frequencyData.length / 3 / barCount));
    drawing.fillStyle = accent;
    drawing.globalAlpha = 0.18;
    for (let bar = 0; bar < barCount; bar += 1) {
      let peak = 0;
      const firstBin = bar * binsPerBar;
      for (let bin = 0; bin < binsPerBar; bin += 1) {
        peak = Math.max(peak, this.frequencyData[firstBin + bin] ?? 0);
      }
      const barHeight = (peak / 255) * height * 0.82;
      const barWidth = width / barCount;
      drawing.fillRect(bar * barWidth, height - barHeight, Math.max(1, barWidth - 1), barHeight);
    }

    drawing.globalAlpha = 1;
    drawing.strokeStyle = accent;
    drawing.lineWidth = 1.5;
    drawing.beginPath();
    const stride = Math.max(1, Math.floor(this.timeData.length / width));
    for (let index = 0; index < this.timeData.length; index += stride) {
      const x = (index / (this.timeData.length - 1)) * width;
      const y = (this.timeData[index] / 255) * height;
      if (index === 0) drawing.moveTo(x, y);
      else drawing.lineTo(x, y);
    }
    drawing.stroke();
  };
}

const visualizer = new OutputVisualizer(visualizerCanvas);
let mediaGraph:
  | { context: AudioContext; analyser: AnalyserNode; source: MediaElementAudioSourceNode }
  | undefined;

const ensureMediaGraph = () => {
  if (!mediaGraph) {
    const context = new AudioContext({ latencyHint: "interactive" });
    const analyser = context.createAnalyser();
    analyser.fftSize = 2048;
    analyser.smoothingTimeConstant = 0.72;
    const source = context.createMediaElementSource(audioPlayer);
    source.connect(analyser);
    analyser.connect(context.destination);
    mediaGraph = { context, analyser, source };
  }
  visualizer.attach(mediaGraph.analyser);
  return mediaGraph;
};

let worker: Worker | undefined;
let currentAudioUrl: string | undefined;
let streamPlayer: StreamPlayer | undefined;
let selectedEngineId = "subaru-ej25";

class StreamPlayer {
  private readonly context = new AudioContext({ latencyHint: "interactive" });
  private readonly analyser = this.context.createAnalyser();
  private readonly queued: Int16Array[] = [];
  private readonly sources = new Set<AudioBufferSourceNode>();
  private readonly createdAt = performance.now();
  private queuedSamples = 0;
  private nextStartTime = 0;
  private started = false;
  private underruns = 0;

  constructor() {
    this.analyser.fftSize = 2048;
    this.analyser.smoothingTimeConstant = 0.72;
    this.analyser.connect(this.context.destination);
  }

  async resume() {
    await this.context.resume();
  }

  push(samples: Int16Array, sampleRate: number, totalSeconds: number) {
    if (samples.length === 0) return;
    if (!this.started) {
      this.queued.push(samples);
      this.queuedSamples += samples.length;
      const generatedSeconds = this.queuedSamples / sampleRate;
      const elapsedSeconds = Math.max(0.05, (performance.now() - this.createdAt) / 1000);
      const measuredRate = generatedSeconds / elapsedSeconds;
      const safeRate = Math.min(0.95, measuredRate * 0.75);
      const continuousThreshold = Math.min(
        totalSeconds,
        Math.max(0.35, totalSeconds * (1 - safeRate) + 0.25),
      );
      if (generatedSeconds >= continuousThreshold) this.startQueued(sampleRate);
      return;
    }
    this.schedule(samples, sampleRate);
  }

  finish(sampleRate: number) {
    if (!this.started && this.queuedSamples > 0) this.startQueued(sampleRate);
  }

  hasStarted() {
    return this.started;
  }

  bufferedSeconds(sampleRate: number) {
    return this.queuedSamples / sampleRate;
  }

  underrunCount() {
    return this.underruns;
  }

  analyserNode() {
    return this.analyser;
  }

  stop() {
    for (const source of this.sources) {
      try {
        source.stop();
      } catch {
        // A source may already have ended between iteration and stop().
      }
    }
    this.sources.clear();
    void this.context.close();
  }

  private startQueued(sampleRate: number) {
    this.started = true;
    for (const samples of this.queued) this.schedule(samples, sampleRate);
    this.queued.length = 0;
    this.queuedSamples = 0;
  }

  private schedule(samples: Int16Array, sampleRate: number) {
    const buffer = this.context.createBuffer(1, samples.length, sampleRate);
    const pcm = buffer.getChannelData(0);
    for (let index = 0; index < samples.length; index += 1) {
      pcm[index] = samples[index] / 32768;
    }

    const earliest = this.context.currentTime + 0.025;
    if (this.nextStartTime > 0 && this.nextStartTime < this.context.currentTime) {
      this.underruns += 1;
    }
    const startsAt = Math.max(this.nextStartTime, earliest);
    const source = this.context.createBufferSource();
    source.buffer = buffer;
    source.connect(this.analyser);
    source.addEventListener("ended", () => this.sources.delete(source));
    this.sources.add(source);
    source.start(startsAt);
    this.nextStartTime = startsAt + buffer.duration;
  }
}

const selectedEngine = (): EngineInfo => engines[selectedEngineId] ?? engines["subaru-ej25"];

const stopStream = () => {
  streamPlayer?.stop();
  streamPlayer = undefined;
};

const setAudio = (url: string, title: string, detail: string, filename?: string, autoplay = true) => {
  if (currentAudioUrl?.startsWith("blob:")) URL.revokeObjectURL(currentAudioUrl);
  currentAudioUrl = url;
  audioPlayer.src = url;
  audioPlayer.hidden = false;
  trackTitle.textContent = title;
  trackDetail.textContent = detail;
  playerPanel.hidden = false;
  if (filename) {
    downloadLink.href = url;
    downloadLink.download = filename;
    downloadLink.hidden = false;
  } else {
    downloadLink.hidden = true;
  }
  const graph = ensureMediaGraph();
  if (autoplay) {
    void graph.context.resume().then(() => audioPlayer.play());
  }
};

const setProgress = (progress: number) => {
  const bounded = Math.min(1, Math.max(0, progress));
  renderProgress.value = bounded;
  const percent = Math.round(bounded * 100);
  progressLabel.value = `${percent}%`;
  statusLabel.textContent = percent < 20 ? "Cranking the engine…" : percent < 85 ? "Resolving exhaust pulses…" : "Finishing the waveform…";
};

const finishWorker = () => {
  worker?.terminate();
  worker = undefined;
  generateButton.disabled = false;
};

const updateEngineUi = () => {
  const info = selectedEngine();
  sampleButton.disabled = !info.sample;
  sampleButton.textContent = info.sample ? `Play clean ${info.name} sample` : "Clean sample unavailable";
  modelState.textContent = "Selected configuration";
  modelEngine.textContent = info.name;
  modelGeometry.textContent = info.metadata;
  modelDisplacement.textContent = "Verified when render starts";
  modelFrequency.textContent = "Verified when render starts";
  modelRouting.textContent = "Verified when render starts";
  modelAcoustics.textContent = "Impulse-response convolution";
  resetTelemetry();
};

const resetTelemetry = () => {
  telemetryRpm.textContent = "—";
  telemetryManifold.textContent = "—";
  telemetryAfr.textContent = "—";
  telemetryCylinder.textContent = "—";
  telemetryExhaust.textContent = "—";
};

const showModelInfo = (engineName: string, model: ModelInfo) => {
  modelState.textContent = "Loaded from Wasm model";
  modelEngine.textContent = `${engineName} · ${model.fuelName}`;
  modelGeometry.textContent = `${model.cylinderCount} cylinders · ${model.cylinderBankCount} ${model.cylinderBankCount === 1 ? "bank" : "banks"}`;
  modelDisplacement.textContent = `${model.displacementLiters.toFixed(3)} L`;
  modelFrequency.textContent = `${model.simulationFrequency.toLocaleString()} Hz`;
  modelRouting.textContent = `${model.intakeCount} ${model.intakeCount === 1 ? "intake" : "intakes"} · ${model.exhaustCount} ${model.exhaustCount === 1 ? "exhaust" : "exhausts"}`;
  modelAcoustics.textContent = `${model.impulseResponseCount} IR ${model.impulseResponseCount === 1 ? "channel" : "channels"} · ${Math.round(model.convolutionMix * 100)}% mix`;
};

const showTelemetry = (telemetry: Telemetry) => {
  telemetryRpm.textContent = `${Math.round(telemetry.rpm).toLocaleString()} rpm`;
  telemetryManifold.textContent = `${telemetry.manifoldPressureKpa.toFixed(1)} kPa`;
  telemetryAfr.textContent = telemetry.intakeAfr > 0 ? telemetry.intakeAfr.toFixed(2) : "—";
  telemetryCylinder.textContent = `${telemetry.cylinderPressureKpa.toFixed(1)} kPa`;
  telemetryExhaust.textContent = `${telemetry.exhaustPressureKpa.toFixed(1)} kPa`;
};

const renderEnginePicker = () => {
  const fragment = document.createDocumentFragment();
  for (const [engineId, info] of Object.entries(engines)) {
    const label = document.createElement("label");
    label.className = "engine-option";

    const input = document.createElement("input");
    input.type = "radio";
    input.name = "engine";
    input.value = engineId;
    input.checked = engineId === selectedEngineId;

    const body = document.createElement("span");
    body.className = "engine-option-body";
    const name = document.createElement("strong");
    name.textContent = info.name;
    const metadata = document.createElement("small");
    metadata.textContent = info.metadata;
    body.append(name, metadata);
    label.append(input, body);
    fragment.append(label);
  }
  enginePicker.replaceChildren(fragment);
};

enginePicker.addEventListener("change", (event) => {
  const input = event.target;
  if (input instanceof HTMLInputElement && input.name === "engine") {
    selectedEngineId = input.value;
    updateEngineUi();
  }
});

throttleRange.addEventListener("input", () => {
  throttleOutput.value = `${throttleRange.value}%`;
});

sampleButton.addEventListener("click", () => {
  const info = selectedEngine();
  if (info.sample) {
    finishWorker();
    stopStream();
    renderStatus.hidden = true;
    modelState.textContent = "Cached render from this model pipeline";
    setAudio(info.sample, info.name, info.sampleDetail ?? "Full-quality render");
  }
});

cancelButton.addEventListener("click", () => {
  finishWorker();
  stopStream();
  audioPlayer.pause();
  audioPlayer.currentTime = 0;
  visualizer.attach();
  renderStatus.hidden = true;
  statusLabel.textContent = "Render cancelled";
});

generateButton.addEventListener("click", async () => {
  finishWorker();
  stopStream();
  const engineId = selectedEngineId;
  const info = engines[engineId] ?? engines["subaru-ej25"];
  const renderSeconds = Number(durationSelect.value);
  const renderThrottle = Number(throttleRange.value) / 100;
  generateButton.disabled = true;
  renderStatus.hidden = false;
  modelState.textContent = "Loading Wasm model…";
  resetTelemetry();
  setProgress(0);
  if (info.sample) {
    setAudio(
      info.sample,
      info.name,
      "Playing clean reference sample · custom render running",
    );
  } else {
    audioPlayer.pause();
    audioPlayer.hidden = true;
    downloadLink.hidden = true;
    playerPanel.hidden = false;
    trackTitle.textContent = info.name;
    trackDetail.textContent = "Rendering until continuous playback is possible…";
    streamPlayer = new StreamPlayer();
    visualizer.attach(streamPlayer.analyserNode());
    await streamPlayer.resume();
  }

  worker = new Worker(new URL("./worker.ts", import.meta.url), { type: "module" });
  worker.addEventListener("message", (event: MessageEvent<WorkerMessage>) => {
    const message = event.data;
    if (message.type === "ready") {
      statusLabel.textContent = `Rendering ${message.engineName}…`;
      showModelInfo(message.engineName, message.model);
    } else if (message.type === "audio") {
      streamPlayer?.push(message.samples, message.sampleRate, renderSeconds);
      if (streamPlayer && !streamPlayer.hasStarted()) {
        trackDetail.textContent = `Buffering clean output · ${streamPlayer.bufferedSeconds(message.sampleRate).toFixed(1)} s ready`;
      } else if (streamPlayer?.hasStarted()) {
        trackDetail.textContent = streamPlayer.underrunCount() > 0
          ? "Playback paused unexpectedly · completed replay will be continuous"
          : "Playing continuously from the render buffer";
      }
    } else if (message.type === "progress") {
      setProgress(message.progress);
      showTelemetry(message.telemetry);
    } else if (message.type === "complete") {
      streamPlayer?.finish(message.sampleRate);
      const blob = new Blob([message.bytes as BlobPart], { type: "audio/wav" });
      const url = URL.createObjectURL(blob);
      const detail = streamPlayer && streamPlayer.underrunCount() > 0
        ? "Render complete · replay is continuous"
        : "Custom render complete · replay or download";
      setAudio(url, message.engineName, detail, info.filename, false);
      if (streamPlayer?.hasStarted()) visualizer.attach(streamPlayer.analyserNode());
      renderStatus.hidden = true;
      finishWorker();
    } else if (message.type === "error") {
      statusLabel.textContent = `Render failed: ${message.message}`;
      progressLabel.value = "—";
      stopStream();
      finishWorker();
    }
  });
  worker.addEventListener("error", (event) => {
    statusLabel.textContent = `Render failed: ${event.message}`;
    progressLabel.value = "—";
    stopStream();
    finishWorker();
  });
  worker.postMessage({
    engineId,
    seconds: renderSeconds,
    throttle: renderThrottle,
  });
});

window.addEventListener("beforeunload", () => {
  worker?.terminate();
  stopStream();
  if (currentAudioUrl?.startsWith("blob:")) URL.revokeObjectURL(currentAudioUrl);
});

audioPlayer.addEventListener("play", () => {
  const graph = ensureMediaGraph();
  void graph.context.resume();
});

renderEnginePicker();
updateEngineUi();
